import React from 'react';
import { Controller } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  FormSelect,
  VirtualizedFormSelect,
  Alert,
  AlertDescription,
  CollapsibleSection,
} from '@roger/ui-components';
import { Loader2, CheckCircle, Users } from 'lucide-react';
import { Field, DateField } from '../../FormFields';
import { isBoolean } from 'lodash-es';
import { formatPhone, formatSSN, formatZipCode } from '../../../utils/intake';
import {
  type PatientMatchResult,
  LANGUAGE_OPTIONS,
} from '../../../hooks/useCreatePatientForm';
import type { CreateEpisodeStepFormData } from '../../../schemas/createEpisodeStepFormSchema';

interface PatientDetailsSectionProps {
  // Form controls
  control: Control<CreateEpisodeStepFormData>;
  register: UseFormRegister<CreateEpisodeStepFormData>;
  errors: FieldErrors<CreateEpisodeStepFormData>;
  setValue: UseFormSetValue<CreateEpisodeStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<CreateEpisodeStepFormData>) => void;

  // Patient matching
  patientMatches: PatientMatchResult[] | undefined;
  setPatientMatchModalOpen: (open: boolean) => void;
  handleNameFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
  isMatchingPatients: boolean;

  // Address validation
  isValidatingAddress: boolean;
  handleAddressFieldBlur: (
    fieldNameOrEvent: React.FocusEvent<HTMLElement>
  ) => void;

  // Language key for conditional rendering
  kinnserLanguageKey: string | null | undefined;

  // Whether we are creating for Roger EMR
  pushToRoger?: boolean;
}

export const PatientDetailsSection: React.FC<PatientDetailsSectionProps> = ({
  control,
  register,
  errors,
  handleFieldBlur,
  saveToServer,
  patientMatches,
  setPatientMatchModalOpen,
  handleNameFieldBlur,
  isMatchingPatients,
  isValidatingAddress,
  handleAddressFieldBlur,
  kinnserLanguageKey,
  pushToRoger,
}) => {
  const hasMatchingPatients = patientMatches
    ? patientMatches?.length > 0
    : null;

  return (
    <CollapsibleSection title='Patient Details'>
      <div className='grid grid-cols-4 gap-3'>
        {/* Row 1: Name */}
        <Field
          label='First Name'
          htmlFor='patient_first_name'
          required
          error={errors.patient_first_name?.message}
          inputProps={register('patient_first_name', {
            onBlur: handleNameFieldBlur,
          })}
        />
        <Field
          label='Middle Name'
          htmlFor='patient_middle_name'
          inputProps={register('patient_middle_name', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Last Name'
          htmlFor='patient_last_name'
          required
          error={errors.patient_last_name?.message}
          inputProps={register('patient_last_name', {
            onBlur: handleNameFieldBlur,
          })}
          extra={
            isMatchingPatients && (
              <div className='text-xs flex items-center gap-1.5 text-primary'>
                <Loader2 className='h-3 w-3 animate-spin' />
                Searching...
              </div>
            )
          }
        />
        <Field
          label='Suffix'
          htmlFor='suffix'
          inputProps={register('patient_suffix', {
            onBlur: handleFieldBlur,
          })}
        />

        {isBoolean(hasMatchingPatients) && !isMatchingPatients && (
          <div className='col-span-4'>
            {hasMatchingPatients ? (
              <Alert
                className='border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors'
                onClick={() => setPatientMatchModalOpen(true)}
              >
                <AlertDescription className='text-amber-800 flex gap-2 items-center'>
                  <Users className='h-4 w-4 text-amber-600' />
                  <span className='font-medium'>
                    {patientMatches!.length} potential duplicate
                    {patientMatches!.length > 1 ? 's' : ''} found
                  </span>
                  <span className='text-amber-600 underline ml-auto'>View</span>
                </AlertDescription>
              </Alert>
            ) : (
              !pushToRoger && (
                <Alert className='border-green-200 bg-green-50'>
                  <AlertDescription className='text-green-800 flex gap-2 items-center'>
                    <CheckCircle className='h-4 w-4 text-green-600' />
                    No duplicate patients found
                  </AlertDescription>
                </Alert>
              )
            )}
          </div>
        )}

        {/* Row 2: Key identifiers */}
        <Controller
          name='patient_dob'
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <DateField
              label='Date of Birth'
              value={value}
              htmlFor='dob'
              required
              warning={!value}
              onChange={val => {
                onChange(val);
                saveToServer({ patient_dob: val });
              }}
              onBlur={onBlur}
              yearRange={{
                fromYear: new Date().getFullYear() - 120,
                toYear: new Date().getFullYear(),
              }}
            />
          )}
        />
        <FormSelect
          label='Gender'
          placeholder='Select gender...'
          options={[
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' },
          ]}
          required
          error={errors.patient_gender?.message}
          {...register('patient_gender', {
            onBlur: handleFieldBlur,
          })}
        />

        <Controller
          name='patient_ssn'
          control={control}
          render={({ field }) => (
            <Field
              label='SSN'
              htmlFor='ssn'
              error={errors.patient_ssn?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatSSN(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
        <Controller
          name='patient_phone'
          control={control}
          render={({ field }) => (
            <Field
              label='Phone'
              htmlFor='phone'
              required
              error={errors.patient_phone?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhone(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
        {/* Row 3: Address */}
        <div className='col-span-2'>
          <Field
            label='Street Address'
            htmlFor='street'
            required
            error={errors.patient_street_address?.message}
            inputProps={register('patient_street_address', {
              onBlur: handleAddressFieldBlur,
            })}
            extra={
              isValidatingAddress && (
                <div className='text-xs flex items-center gap-1.5 text-primary'>
                  <Loader2 className='h-3 w-3 animate-spin' />
                  Validating...
                </div>
              )
            }
          />
        </div>
        <Field
          label='City'
          htmlFor='city'
          required
          error={errors.patient_city?.message}
          inputProps={register('patient_city', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='State'
          htmlFor='state'
          required
          error={errors.patient_state?.message}
          inputProps={register('patient_state', {
            onBlur: handleFieldBlur,
          })}
        />
        {/* Row 4: Additional details */}
        <Controller
          name='patient_zip_code'
          control={control}
          render={({ field }) => (
            <Field
              label='Zip Code'
              htmlFor='zip'
              required
              error={errors.patient_zip_code?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatZipCode(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur();
                  handleAddressFieldBlur(e);
                },
              }}
            />
          )}
        />
        <Field
          label='County'
          htmlFor='county'
          inputProps={register('patient_county', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Email'
          htmlFor='email'
          error={errors.patient_email?.message}
          inputProps={register('patient_email', {
            onBlur: handleFieldBlur,
          })}
        />
        {!pushToRoger && (
          <>
            <VirtualizedFormSelect
              label='Language'
              placeholder='Select language'
              options={LANGUAGE_OPTIONS}
              required
              error={errors.kinnser_language_key?.message}
              {...register('kinnser_language_key', {
                onBlur: handleFieldBlur,
              })}
            />
            {kinnserLanguageKey === '6' && (
              <Field
                label='Other Language'
                htmlFor='kinnser_other_language'
                required
                error={errors.kinnser_other_language?.message}
                inputProps={register('kinnser_other_language', {
                  onBlur: handleFieldBlur,
                })}
              />
            )}
          </>
        )}
      </div>
    </CollapsibleSection>
  );
};
